import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Platform, Modal } from 'react-native';
import AppTextInput from './AppTextInput';
import type { AutomotiveIntakeData, VehicleHandoff } from '../types/automotiveIntake';
import { MANUAL_VEHICLE_ENTRY } from '../types/automotiveIntake';
import type { VehicleProfile } from '../types/vehicle';
import { formatVehicleLabel } from '../types/vehicle';

interface AutomotiveServiceIntakeFormProps {
  value: AutomotiveIntakeData;
  onChange: (next: AutomotiveIntakeData) => void;
  /** Logged-in users: vehicles from My Garage */
  savedVehicles?: VehicleProfile[];
  /** Selected garage vehicle id, or MANUAL_VEHICLE_ENTRY for typed entry */
  selectedSavedVehicleId?: string | null;
  onSelectSavedVehicle?: (vehicle: VehicleProfile) => void;
  onChooseManualEntry?: () => void;
  /** Opens My Vehicles manager (Profile garage) */
  onManageVehicles?: () => void;
  compact?: boolean;
}

const AutomotiveServiceIntakeForm: React.FC<AutomotiveServiceIntakeFormProps> = ({
  value,
  onChange,
  savedVehicles = [],
  selectedSavedVehicleId = null,
  onSelectSavedVehicle,
  onChooseManualEntry,
  onManageVehicles,
  compact = false,
}) => {
  const [pickerVisible, setPickerVisible] = useState(false);

  const setField = <K extends keyof AutomotiveIntakeData>(key: K, fieldValue: AutomotiveIntakeData[K]) => {
    onChange({ ...value, [key]: fieldValue });
  };

  const setHandoff = (handoff: VehicleHandoff) => {
    onChange({ ...value, vehicleHandoff: handoff });
  };

  const showGaragePicker = savedVehicles.length > 0 && !!onSelectSavedVehicle;

  const pickerLabel = useMemo(() => {
    if (!showGaragePicker) return null;
    if (selectedSavedVehicleId && selectedSavedVehicleId !== MANUAL_VEHICLE_ENTRY) {
      const vehicle = savedVehicles.find((v) => v.id === selectedSavedVehicleId);
      if (vehicle) return formatVehicleLabel(vehicle);
    }
    return 'Enter a different vehicle';
  }, [showGaragePicker, selectedSavedVehicleId, savedVehicles]);

  const handlePickVehicle = (vehicle: VehicleProfile) => {
    onSelectSavedVehicle?.(vehicle);
    setPickerVisible(false);
  };

  const handlePickManual = () => {
    onChooseManualEntry?.();
    setPickerVisible(false);
  };

  return (
    <View style={[styles.container, compact && styles.containerCompact]}>
      <View style={styles.titleRow}>
        <Text style={styles.sectionTitle}>Vehicle & service details</Text>
        {onManageVehicles ? (
          <TouchableOpacity onPress={onManageVehicles} style={styles.manageLink}>
            <Text style={styles.manageLinkText}>My Garage</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <Text style={styles.sectionHint}>
        Required for auto care bookings so the shop can prepare your bay and contact you when ready.
      </Text>

      {showGaragePicker ? (
        <View style={styles.field}>
          <Text style={styles.label}>Select vehicle *</Text>
          <TouchableOpacity
            style={styles.pickerTrigger}
            onPress={() => setPickerVisible(true)}
            accessibilityRole="button"
            accessibilityLabel="Select saved vehicle"
          >
            <Text style={styles.pickerTriggerText}>{pickerLabel}</Text>
            <Text style={styles.pickerChevron}>▼</Text>
          </TouchableOpacity>
          <Text style={styles.pickerHint}>
            Choose a saved vehicle to auto-fill details, or enter a different vehicle.
          </Text>
          {selectedSavedVehicleId &&
          selectedSavedVehicleId !== MANUAL_VEHICLE_ENTRY ? (
            <Text style={styles.autoFillNote}>
              Fields below are filled from your saved vehicle. Tap a field to change anything.
            </Text>
          ) : null}
        </View>
      ) : onManageVehicles ? (
        <View style={styles.garageEmptyBanner}>
          <Text style={styles.garageEmptyText}>
            Save vehicles in My Garage to auto-fill this form on your next booking.
          </Text>
          <TouchableOpacity style={styles.garageEmptyBtn} onPress={onManageVehicles}>
            <Text style={styles.garageEmptyBtnText}>Add a vehicle</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <Text style={styles.label}>Will you drop off your vehicle or wait during service? *</Text>
      <View style={styles.handoffRow}>
        <TouchableOpacity
          style={[styles.handoffBtn, value.vehicleHandoff === 'drop_off' && styles.handoffBtnActive]}
          onPress={() => setHandoff('drop_off')}
          accessibilityRole="button"
          accessibilityState={{ selected: value.vehicleHandoff === 'drop_off' }}
        >
          <Text
            style={[
              styles.handoffBtnText,
              value.vehicleHandoff === 'drop_off' && styles.handoffBtnTextActive,
            ]}
          >
            Drop off
          </Text>
          <Text style={styles.handoffSubtext}>Leave the car; pick up later</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.handoffBtn, value.vehicleHandoff === 'wait' && styles.handoffBtnActive]}
          onPress={() => setHandoff('wait')}
          accessibilityRole="button"
          accessibilityState={{ selected: value.vehicleHandoff === 'wait' }}
        >
          <Text
            style={[styles.handoffBtnText, value.vehicleHandoff === 'wait' && styles.handoffBtnTextActive]}
          >
            Wait on-site
          </Text>
          <Text style={styles.handoffSubtext}>Stay while it&apos;s serviced</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.row}>
        <View style={[styles.field, styles.fieldSmall]}>
          <Text style={styles.label}>Year *</Text>
          <AppTextInput
            style={styles.input}
            value={value.year}
            onChangeText={(text) => setField('year', text)}
            placeholder="e.g., 2020"
            keyboardType="number-pad"
            maxLength={4}
          />
        </View>
        <View style={[styles.field, styles.fieldGrow]}>
          <Text style={styles.label}>Make *</Text>
          <AppTextInput
            style={styles.input}
            value={value.make}
            onChangeText={(text) => setField('make', text)}
            placeholder="e.g., Toyota"
            autoCapitalize="words"
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={[styles.field, styles.fieldGrow]}>
          <Text style={styles.label}>Model *</Text>
          <AppTextInput
            style={styles.input}
            value={value.model}
            onChangeText={(text) => setField('model', text)}
            placeholder="e.g., Camry"
            autoCapitalize="words"
          />
        </View>
        <View style={[styles.field, styles.fieldGrow]}>
          <Text style={styles.label}>License plate *</Text>
          <AppTextInput
            style={styles.input}
            value={value.licensePlate}
            onChangeText={(text) => setField('licensePlate', text.toUpperCase())}
            placeholder="e.g., ABC 1234"
            autoCapitalize="characters"
          />
        </View>
      </View>

      <View style={styles.field}>
        <Text style={styles.label}>VIN (optional)</Text>
        <AppTextInput
          style={styles.input}
          value={value.vin}
          onChangeText={(text) => setField('vin', text.toUpperCase())}
          placeholder="e.g., 17-character VIN"
          autoCapitalize="characters"
          maxLength={17}
        />
      </View>

      <View style={styles.field}>
        <Text style={styles.label}>Additional details (optional)</Text>
        <AppTextInput
          style={[styles.input, styles.textArea]}
          value={value.additionalDetails}
          onChangeText={(text) => setField('additionalDetails', text)}
          placeholder="e.g., Describe symptoms, shuttle need, etc."
          multiline
          numberOfLines={3}
          textAlignVertical="top"
        />
      </View>

      <Modal
        visible={pickerVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setPickerVisible(false)}
      >
        <TouchableOpacity
          style={styles.pickerOverlay}
          activeOpacity={1}
          onPress={() => setPickerVisible(false)}
        >
          <View style={styles.pickerSheet} onStartShouldSetResponder={() => true}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>Select vehicle</Text>
              <TouchableOpacity onPress={() => setPickerVisible(false)}>
                <Text style={styles.pickerClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.pickerList}>
              {savedVehicles.map((vehicle) => {
                const selected = selectedSavedVehicleId === vehicle.id;
                return (
                  <TouchableOpacity
                    key={vehicle.id}
                    style={[styles.pickerOption, selected && styles.pickerOptionSelected]}
                    onPress={() => handlePickVehicle(vehicle)}
                  >
                    <Text style={styles.pickerOptionText}>
                      {formatVehicleLabel(vehicle)}
                      {vehicle.plate ? ` · ${vehicle.plate}` : ''}
                      {vehicle.isDefault ? ' (Default)' : ''}
                    </Text>
                    {selected ? <Text style={styles.pickerCheck}>✓</Text> : null}
                  </TouchableOpacity>
                );
              })}
              <TouchableOpacity
                style={[
                  styles.pickerOption,
                  styles.pickerManualOption,
                  selectedSavedVehicleId === MANUAL_VEHICLE_ENTRY && styles.pickerOptionSelected,
                ]}
                onPress={handlePickManual}
              >
                <Text style={styles.pickerOptionText}>Enter a different vehicle</Text>
                {selectedSavedVehicleId === MANUAL_VEHICLE_ENTRY ? (
                  <Text style={styles.pickerCheck}>✓</Text>
                ) : null}
              </TouchableOpacity>
            </ScrollView>
            {onManageVehicles ? (
              <TouchableOpacity style={styles.pickerManageBtn} onPress={() => {
                setPickerVisible(false);
                onManageVehicles();
              }}>
                <Text style={styles.pickerManageBtnText}>Manage My Garage</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
  },
  containerCompact: {
    marginTop: 0,
    paddingTop: 0,
    borderTopWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1a1a1a',
    flex: 1,
  },
  manageLink: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  manageLinkText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#007AFF',
  },
  sectionHint: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 14,
    lineHeight: 18,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#334155',
    marginBottom: 6,
  },
  pickerTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'web' ? 12 : 14,
    backgroundColor: '#fff',
  },
  pickerTriggerText: {
    fontSize: 15,
    color: '#1a1a1a',
    flex: 1,
    paddingRight: 8,
  },
  pickerChevron: {
    fontSize: 12,
    color: '#64748b',
  },
  pickerHint: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 6,
    lineHeight: 16,
  },
  autoFillNote: {
    fontSize: 12,
    color: '#007AFF',
    marginTop: 8,
    lineHeight: 16,
    fontWeight: '600',
  },
  garageEmptyBanner: {
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    marginBottom: 14,
  },
  garageEmptyText: {
    fontSize: 13,
    color: '#475569',
    lineHeight: 18,
    marginBottom: 10,
  },
  garageEmptyBtn: {
    alignSelf: 'flex-start',
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  garageEmptyBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#007AFF',
  },
  handoffRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  },
  handoffBtn: {
    flex: 1,
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 10,
    backgroundColor: '#fff',
  },
  handoffBtnActive: {
    borderColor: '#007AFF',
    backgroundColor: '#EFF6FF',
  },
  handoffBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#334155',
    textAlign: 'center',
  },
  handoffBtnTextActive: {
    color: '#007AFF',
  },
  handoffSubtext: {
    fontSize: 11,
    color: '#64748b',
    textAlign: 'center',
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    gap: 10,
  },
  field: {
    flex: 1,
    marginBottom: 12,
  },
  fieldSmall: {
    flex: 0.45,
  },
  fieldGrow: {
    flex: 1,
  },
  input: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'web' ? 10 : 12,
    fontSize: 15,
    color: '#1a1a1a',
    backgroundColor: '#fff',
  },
  textArea: {
    minHeight: 84,
  },
  pickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  pickerSheet: {
    backgroundColor: '#fff',
    borderRadius: 16,
    maxHeight: '70%',
    width: '100%',
    maxWidth: 420,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
    overflow: 'hidden',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  pickerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1a1a1a',
  },
  pickerClose: {
    fontSize: 20,
    color: '#64748b',
    padding: 4,
  },
  pickerList: {
    maxHeight: 320,
  },
  pickerOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E2E8F0',
  },
  pickerOptionSelected: {
    backgroundColor: '#EFF6FF',
  },
  pickerManualOption: {
    borderTopWidth: 8,
    borderTopColor: '#F1F5F9',
  },
  pickerOptionText: {
    fontSize: 15,
    color: '#1a1a1a',
    flex: 1,
    paddingRight: 8,
  },
  pickerCheck: {
    fontSize: 16,
    fontWeight: '700',
    color: '#007AFF',
  },
  pickerManageBtn: {
    marginHorizontal: 16,
    marginTop: 8,
    paddingVertical: 12,
    alignItems: 'center',
  },
  pickerManageBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#007AFF',
  },
});

export default AutomotiveServiceIntakeForm;
